
import React, { useState, useEffect } from 'react';
import { UserProfile, CommunityRoom, CommunityPost, CompanionMatch, Language } from '../types';
import { findCompanionMatch } from '../services/geminiService';
import { TRANSLATIONS } from '../translations';
import { Users, Globe, Briefcase, Baby, Plane, MessageSquare, ShieldCheck, Flag, Heart, Send, UserPlus, Loader2, Lock, ArrowRight, Zap } from 'lucide-react';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

const CommunityHub: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState<'rooms' | 'companions'>('rooms');
  const [selectedRoom, setSelectedRoom] = useState<CommunityRoom | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [matchingLoading, setMatchingLoading] = useState(false);
  
  const t = TRANSLATIONS[user.language] || TRANSLATIONS[Language.KURDISH];

  const rooms: CommunityRoom[] = [
    { id: 'parents', name: user.language === Language.KURDISH ? 'کۆمەڵەی دایکان و باوکان' : 'انجمن والدین', description: user.language === Language.KURDISH ? 'هاوبەشی ئەزموون لەسەر پەروەردە' : 'اشتراک تجربه درباره تربیت فرزند', members: 1240, icon: '👨‍👩‍👧‍👦' },
    { id: 'startup', name: user.language === Language.KURDISH ? 'کارسازی و ستارتەپ' : 'کارآفرینی و استارتاپ', description: user.language === Language.KURDISH ? 'گرووپی ئاڵوگۆڕی بیرۆکەی بازرگانی' : 'گروه تبادل ایده‌های تجاری', members: 850, icon: '🚀' },
    { id: 'lang', name: user.language === Language.KURDISH ? 'زمان و فێربوون' : 'زبان و یادگیری', description: user.language === Language.KURDISH ? 'مەشقکردن لەسەر زمانە جیاوازەکان' : 'تمرین زبان‌های مختلف', members: 2100, icon: '🗣️' },
    { id: 'travel', name: user.language === Language.KURDISH ? 'گەشتیارانی هاوڕێ' : 'گردشگران هاورێ', description: user.language === Language.KURDISH ? 'بۆ دۆزینەوەی باشترین شوێنەکان' : 'برای یافتن بهترین مکان‌ها', members: 1600, icon: '✈️' },
  ];

  const [roomPosts, setRoomPosts] = useState<Record<string, CommunityPost[]>>({
      'parents': [
          { id: '1', roomId: 'parents', authorName: user.language === Language.KURDISH ? 'بەکارهێنەر-١٠٢' : 'کاربر-۱۰۲', authorColor: 'bg-green-500', content: user.language === Language.KURDISH ? 'چۆن لەگەڵ ماندوێتی دوای کار ڕادێن؟' : 'چطور با خستگی بعد از کار کنار میاید؟', timestamp: Date.now() - 3600000, likes: 12, isTrusted: true },
      ]
  });

  const handleSendMessage = () => {
      if (!messageInput.trim() || !selectedRoom) return;
      const newPost: CommunityPost = {
          id: Date.now().toString(),
          roomId: selectedRoom.id,
          authorName: user.communityProfile.anonymousName,
          authorColor: user.communityProfile.anonymousColor,
          content: messageInput,
          timestamp: Date.now(),
          likes: 0,
          isTrusted: user.communityProfile.trustScore > 50
      };
      setRoomPosts(prev => ({ ...prev, [selectedRoom.id]: [...(prev[selectedRoom.id] || []), newPost] }));
      setMessageInput('');
  };

  const handleFindCompanion = async () => {
      setMatchingLoading(true);
      try {
          const match = await findCompanionMatch(user);
          if (match) {
              const updatedUser = { ...user };
              updatedUser.communityProfile.matches.push(match);
              onUpdateUser(updatedUser);
          }
      } catch (e) {
          console.error(e);
      } finally {
          setMatchingLoading(false);
      }
  };

  const formatTime = (ts: number) => {
      return new Date(ts).toLocaleTimeString(user.language === Language.KURDISH ? 'ckb-IR' : 'fa-IR', { hour: '2-digit', minute: '2-digit' });
  };

  if (selectedRoom) {
      const posts = roomPosts[selectedRoom.id] || [];
      return (
          <div className="h-full flex flex-col bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden animate-fade-in">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                  <div className="flex items-center gap-3">
                      <button 
                        onClick={() => setSelectedRoom(null)} 
                        className="p-2 hover:bg-white rounded-full transition-colors flex items-center gap-2 text-slate-500 font-bold"
                      >
                          <ArrowRight className="w-5 h-5" />
                          <span className="text-sm">{t.back}</span>
                      </button>
                      <div>
                          <h3 className="font-bold text-slate-800 flex items-center gap-2">{selectedRoom.icon} {selectedRoom.name}</h3>
                          <span className="text-xs text-slate-500">{t.identity_protected}</span>
                      </div>
                  </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50 no-scrollbar">
                  {posts.length === 0 ? (
                      <div className="text-center text-slate-400 mt-10">
                          <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-20" />
                          <p>{t.comm_first_msg}</p>
                      </div>
                  ) : (
                      posts.map(post => {
                          const isMe = post.authorName === user.communityProfile.anonymousName;
                          return (
                              <div key={post.id} className={`flex gap-3 ${isMe ? 'flex-row-reverse' : ''}`}>
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0 ${post.authorColor}`}>{post.authorName.charAt(0)}</div>
                                  <div className={`max-w-[80%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                                      <div className="flex items-center gap-2 mb-1 px-1">
                                          <span className="text-xs font-bold text-slate-600">{post.authorName}</span>
                                          <span className="text-[10px] text-slate-400">{formatTime(post.timestamp)}</span>
                                      </div>
                                      <div className={`p-4 rounded-2xl text-sm leading-relaxed shadow-sm ${isMe ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-white text-slate-800 border border-slate-200 rounded-tl-none'}`}>{post.content}</div>
                                      <div className="flex items-center gap-3 mt-1 px-2">
                                          <button className="text-slate-400 hover:text-red-500 text-xs flex items-center gap-1"><Heart className="w-3 h-3" /> {post.likes}</button>
                                      </div>
                                  </div>
                              </div>
                          );
                      })
                  )}
              </div>
              <div className="p-4 bg-white border-t border-slate-100">
                  <div className="flex gap-2">
                      <input 
                        value={messageInput} 
                        onChange={(e) => setMessageInput(e.target.value)} 
                        onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()} 
                        placeholder={t.comm_type_msg} 
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-100" 
                      />
                      <button 
                        onClick={handleSendMessage} 
                        disabled={!messageInput.trim()} 
                        aria-label={t.send}
                        className="bg-indigo-600 text-white p-3 px-5 rounded-xl shadow-lg flex items-center gap-2"
                      >
                        <Send className="w-5 h-5 rtl:-rotate-90" />
                        <span className="hidden sm:inline font-bold">{t.send}</span>
                      </button>
                  </div>
              </div>
          </div>
      );
  }

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20">
        <div className="flex items-center justify-between mb-6">
            <div>
                <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                    {activeTab === 'rooms' ? <Users className="w-6 h-6 text-indigo-500" /> : <Zap className="w-6 h-6 text-amber-500" />}
                    {activeTab === 'rooms' ? t.safe_community : t.companions_tab}
                </h2>
                <p className="text-slate-500 text-sm mt-1">
                    {activeTab === 'rooms' ? t.anonymous_chat_desc : t.comm_find_companion}
                </p>
            </div>
            <div className="flex bg-white rounded-xl border border-slate-200 p-1">
                <button onClick={() => setActiveTab('rooms')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'rooms' ? 'bg-slate-100 text-slate-800' : 'text-slate-500'}`}>{t.rooms_tab}</button>
                <button onClick={() => setActiveTab('companions')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'companions' ? 'bg-slate-100 text-slate-800' : 'text-slate-500'}`}>{t.companions_tab}</button>
            </div>
        </div>

        {activeTab === 'rooms' ? (
            <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {rooms.map(room => (
                        <button key={room.id} onClick={() => setSelectedRoom(room)} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all text-right flex items-center gap-4 group">
                            <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center text-3xl group-hover:scale-110 transition-transform">{room.icon}</div>
                            <div className="flex-1">
                                <h3 className="font-bold text-slate-800 text-lg mb-1">{room.name}</h3>
                                <p className="text-slate-500 text-sm">{room.description}</p>
                                <div className="flex items-center gap-2 mt-3 text-xs text-indigo-500 font-medium">
                                    <Users className="w-4 h-4" />
                                    {t.comm_active_members.replace('{count}', room.members.toLocaleString(user.language === Language.KURDISH ? 'ckb-IR' : 'fa-IR'))}
                                </div>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 rotate-180 transition-colors" />
                                <span className="text-[8px] text-slate-400 font-bold uppercase">{t.continue}</span>
                            </div>
                        </button>
                    ))}
                </div>

                <div className="mt-8 bg-slate-50 rounded-2xl p-6 border border-slate-200 text-center">
                    <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                    <h4 className="font-bold text-slate-700">{t.identity_protected}</h4>
                    <p className="text-slate-500 text-sm mt-1 max-w-md mx-auto">
                        {t.anonymous_name_is.replace('{name}', user.communityProfile.anonymousName)}
                    </p>
                </div>
            </>
        ) : (
            <div className="space-y-6">
                <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-8 text-white text-center shadow-lg relative overflow-hidden">
                    <h3 className="text-2xl font-bold mb-4 relative z-10">{t.comm_find_companion}</h3>
                    <p className="opacity-90 mb-8 max-w-md mx-auto relative z-10">{t.comm_companion_desc}</p>
                    <button onClick={handleFindCompanion} disabled={matchingLoading} className="bg-white text-indigo-700 px-8 py-3 rounded-xl font-bold hover:bg-indigo-50 transition-colors inline-flex items-center gap-2 shadow-xl">
                        {matchingLoading ? <Loader2 className="animate-spin" /> : <UserPlus className="w-5 h-5" />}
                        {matchingLoading ? t.comm_searching : t.comm_find_btn}
                    </button>
                </div>
            </div>
        )}
    </div>
  );
};

export default CommunityHub;
